import React, { useEffect, useState } from "react";
import { api, fmtINR, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash } from "@phosphor-icons/react";

export default function ApprovalRules() {
  const [rules, setRules] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({
    name: "", module: "expense", min_amount: 0, max_amount: 999999, required_role: "owner", level: 1,
  });

  const load = async () => setRules((await api.get("/approval-rules")).data);
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/approval-rules", { ...form,
        min_amount: Number(form.min_amount), max_amount: Number(form.max_amount),
        level: Number(form.level),
      });
      toast.success("Rule created");
      setShow(false);
      setForm({ name: "", module: "expense", min_amount: 0, max_amount: 999999, required_role: "owner", level: 1 });
      load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!confirm("Delete rule?")) return;
    await api.delete(`/approval-rules/${id}`);
    load();
  };

  return (
    <div className="space-y-5 lb-fade" data-testid="approval-rules-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="lb-section-head">Governance</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">Approval Chains</h1>
          <p className="text-neutral-600 text-sm mt-1">Route expenses/invoices/purchases through role-based approval based on amount thresholds.</p>
        </div>
        <button className="lb-btn" onClick={() => setShow((s) => !s)} data-testid="add-rule-btn"><Plus size={14} /> New Rule</button>
      </div>

      {show && (
        <form onSubmit={submit} className="lb-card p-6 grid grid-cols-6 gap-3">
          <input required className="lb-input col-span-2" placeholder="Rule name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="rule-name" />
          <select className="lb-select" value={form.module} onChange={(e) => setForm({ ...form, module: e.target.value })}>
            <option value="expense">Expense</option>
            <option value="purchase">Purchase</option>
            <option value="invoice">Invoice</option>
          </select>
          <input type="number" required className="lb-input font-mono text-right" placeholder="Min ₹" value={form.min_amount} onChange={(e) => setForm({ ...form, min_amount: e.target.value })} data-testid="rule-min" />
          <input type="number" required className="lb-input font-mono text-right" placeholder="Max ₹" value={form.max_amount} onChange={(e) => setForm({ ...form, max_amount: e.target.value })} data-testid="rule-max" />
          <select className="lb-select" value={form.required_role} onChange={(e) => setForm({ ...form, required_role: e.target.value })} data-testid="rule-role">
            <option value="owner">Owner</option>
            <option value="accountant">Accountant</option>
          </select>
          <div className="col-span-2 flex items-center gap-2">
            <label className="lb-label m-0">Level</label>
            <input type="number" min="1" max="5" className="lb-input font-mono text-right w-20" value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })} />
            <span className="text-xs text-neutral-500">1 = single approver · 2+ = chain</span>
          </div>
          <div className="col-span-6 flex gap-2"><button className="lb-btn" data-testid="rule-submit">Save</button><button type="button" className="lb-btn lb-btn-ghost" onClick={() => setShow(false)}>Cancel</button></div>
        </form>
      )}

      <div className="lb-card">
        <table className="lb-table">
          <thead><tr><th>Rule</th><th>Module</th><th className="text-right">Min</th><th className="text-right">Max</th><th>Required Role</th><th className="text-right">Level</th><th></th></tr></thead>
          <tbody>
            {rules.map((r) => (
              <tr key={r.id}>
                <td className="font-semibold">{r.name}</td>
                <td><span className="lb-badge">{r.module}</span></td>
                <td className="amt">{fmtINR(r.min_amount)}</td>
                <td className="amt">{fmtINR(r.max_amount)}</td>
                <td><span className={"lb-badge " + (r.required_role === "owner" ? "lb-badge-dark" : "lb-badge-info")}>{r.required_role}</span></td>
                <td className="num font-bold">L{r.level}</td>
                <td><button onClick={() => del(r.id)} className="text-neutral-500 hover:text-rose-600"><Trash size={14} /></button></td>
              </tr>
            ))}
            {!rules.length && <tr><td colSpan={7} className="text-center py-8 text-neutral-500">No approval rules configured. Currently: any owner/accountant can approve.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="lb-panel-info p-4 text-xs">
        <b>How it works:</b> When someone submits an expense, the system finds the rule where <code>min_amount ≤ amount ≤ max_amount</code>. The highest-level matching rule dictates the required approver role. Rules with higher <code>level</code> take precedence.
      </div>
    </div>
  );
}
